/**
 * MetricCard displays a single summary metric using Meridian's Card + Metric
 * components, with TrendText for period-over-period comparison.
 * Follows the Meridian Reference App DeliveryTotalCard pattern.
 */
import React from 'react';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Metric from '@amzn/meridian/metric';
import Text from '@amzn/meridian/text';
import TrendText from '@amzn/meridian/trend-text';
import { useTranslation } from 'src/arb-wrapper';

interface MetricCardProps {
  value: number | string;
  label: string;
  isLoading?: boolean;
  /** Previous period value — if provided, shows a TrendText with the difference */
  previousValue?: number;
  /** Unit label shown next to the metric (e.g., "campaigns", "countries") */
  units?: string;
  /** Intl options passed to Meridian Metric while keeping the value numeric */
  numberFormat?: Intl.NumberFormatOptions;
  /** Click handler — also enables the hover/interactive card styling */
  onClick?: () => void;
  /** If true, up is negative (red) and down is positive (green) — use for failure rates */
  invertTrend?: boolean;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  value,
  label,
  isLoading,
  previousValue,
  units,
  numberFormat,
  onClick,
  invertTrend = false,
}) => {
  const { t } = useTranslation();
  const diff =
    previousValue !== undefined && typeof value === 'number'
      ? value - previousValue
      : 0;
  const showTrend =
    previousValue !== undefined && typeof value === 'number' && diff !== 0;
  const arrowDirection = diff > 0 ? 'up' : 'down';
  const trendColor = invertTrend
    ? diff > 0
      ? 'negative'
      : 'positive'
    : diff > 0
      ? 'positive'
      : 'negative';

  return (
    <Card minWidth="min-content" spacingInset="450" onClick={onClick}>
      <Column alignmentVertical="center" height="100%" spacing="100">
        <Text color="secondary" type="h100">
          {label}
        </Text>

        {isLoading
          ? (
          <Column
            alignmentHorizontal="center"
            alignmentVertical="center"
            height="100%"
            minHeight={80}
            width="100%"
          >
            <Loader size="medium" />
          </Column>
            )
          : (
          <>
            <Metric numberFormat={numberFormat} units={units}>
              {typeof value === 'number' ? value : String(value)}
            </Metric>
            {showTrend
              ? (
              <TrendText arrow={arrowDirection} color={trendColor}>
                {t('metric_vs_prev_period', { diff: Math.abs(diff) })}
              </TrendText>
                )
              : previousValue !== undefined && typeof value === 'number'
                ? (
              <Text color="secondary" type="h100">
                {t('metric_no_change')}
              </Text>
                  )
                : null}
          </>
            )}
      </Column>
    </Card>
  );
};
